// FileViewer.jsx
// Context used by the FileViewer components to share state and functions.

// Copyright HS Analysis GmbH, 2023
// Authors: Jakob Burghardt, Viktor Eberhardt, Valentin Haas

// Framework imports
import React from "react";
import PropTypes from "prop-types";
import { useLocation, useParams } from "react-router-dom";

// Material UI imports
import { Grid, Backdrop, CircularProgress } from "@mui/material";
import { makeStyles } from "@mui/styles";

// HSA KIT Components
import Backend from "../common/utils/Backend";
import FileViewerSideBar from "./components/FileViewerSideBar";
import FileViewerSplitscreenManager from "./components/FileViewerSplitscreenManager";
import FileFormatUtils from "../common/utils/FileFormatUtils";

// HSA Imports
import { withPersistentStorage } from "../viewer/contexts/PersistentStorageContext";
import { withTiles } from "../viewer/contexts/TilesContext";
import CommentTool from "../viewer/components/tools/CommentTool";
import EventBus from "../common/services/EventBus";
import ViewHeatMapTool from "../viewer/components/tools/ViewHeatMapTool";

const useStyles = makeStyles(() => ({
  root: {
    width: "100%",
    height: "100%",
    background: "#EBEBEB",
    overflow: "hidden",
    position: "relative",
    display: "grid",
    gridTemplateColumns: "1fr auto;",
  },
}));

export const FileViewerContext = React.createContext();

/**
 * Enum-like object for file sorting types.
 * @readonly
 * @enum {string}
 * @property {string} Name - Sort by file name.
 * @property {string} Date - Sort by file creation date.
 * @property {string} ChangeDate - Sort by file modification date.
 * @property {string} Type - Sort by file ending type (e.g., .tif, .png).
 */
export const SortByType = Object.freeze({
  Name: "name",
  AdditionalName: "additionalName",
  Date: "date",
  ChangeDate: "changeDate",
  Type: "type",
});

/**
 * Enum-like object representing the available tools in the File Viewer.
 * @readonly
 * @enum {string}
 * @property {string} Comment - Tool for adding comments.
 * @property {string} Stream - Tool for streaming Renderer canvas.
 * @property {string} None - No tool selected.
 */
export const FileViewerTools = Object.freeze({
  Comment: "comment",
  ViewHeatMap: "ViewHeatMap",
  Stream: "stream",
  None: "none",
});

function FileViewer(props) {
  const tools = React.useMemo(
    () => ({
      [FileViewerTools.Comment]: new CommentTool(),
      [FileViewerTools.ViewHeatMap]: new ViewHeatMapTool(),
      [FileViewerTools.None]: null,
    }),
    [],
  );
  const classes = useStyles();
  const { caseUrlId } = useParams();
  const location = useLocation();

  const rendererDictRef = React.useRef({});

  //case to fileId handling
  const [caseId, setCaseId] = React.useState(undefined);
  const [caseList, setCaseList] = React.useState({});
  const [omeDict, setOmeDict] = React.useState({});
  const [pathList, setPathList] = React.useState([]);
  const [pending, setPending] = React.useState(true);
  const [sceneList, setSceneList] = React.useState({});
  const [selCase, setSelCase] = React.useState(null);
  const [selectedFolder, setSelectedFolder] = React.useState(null);

  //renderer stuff
  const [userId, setUserId] = React.useState(null);
  const [activeFileId, setActiveFileId] = React.useState(null);
  const [activeFileName, setActiveFileName] = React.useState(null);
  const [activeFilePath, setActiveFilePath] = React.useState(null);
  const [containerKey, setContainerKey] = React.useState(new Date().getTime());
  const [histogramConfigs, setHistogramConfigs] = React.useState({});
  const [importProgress, setImportProgress] = React.useState(-1);
  const [processingRotation, setProcessingRotation] = React.useState(false);
  const [rendererDict, setRendererDict] = React.useState({});
  const [splitscreenFileIds, setSplitscreenFileIds] = React.useState([]);
  const [chainListFileIds, setChainListFileIds] = React.useState([]);
  const [zoomObjectDict, setZoomObjectDict] = React.useState({});
  const [layoutStateObject, setLayoutStateObject] = React.useState(() => {
    const stored = localStorage.getItem("hsaFileViewerLayoutState");
    return stored
      ? JSON.parse(stored)
      : {
          fileNavButtons: false,
          imageInfo: true,
          macroImage: true,
          miniMap: true,
          scaleBar: true,
          slideLabel: true,
          timeBar: true,
          zoomBar: true,
          zStackBar: true,
        };
  });
  const [commentLayer, setCommentLayer] = React.useState({});
  const [commentRois, setCommentRois] = React.useState([]);
  const [activeTool, setActiveTool] = React.useState(FileViewerTools.None);
  const [tool, setTool] = React.useState(null);
  const [sceneDataList, setSceneDataList] = React.useState([]);
  const [heatMapOpacity, setHeatMapOpacity] = React.useState(50);
  const [isScenesLoading, setIsScenesLoading] = React.useState(false);
  // Temporary, non-persisted UI suppressions (e.g., while screenshot dialog is open)
  const [tempHideScaleBar, setTempHideScaleBar] = React.useState(false);
  // Note: History sync disabled — rely on path-based navigation only

  //layout stuff
  const [oldPreviewWidth, setOldPreviewWidth] = React.useState(0);
  const [previewWidth, setPreviewWidth] = React.useState(210);
  const [treeWidth, setTreeWidth] = React.useState(() => {
    const storedWidth = localStorage.getItem("fileViewerSidebarWidth");
    return storedWidth ? parseInt(storedWidth, 10) : 526;
  });
  // In popup/new-window mode, we keep the dock but may start minimized
  const [toolbarOnlySidebar] = React.useState(false);
  const [startSidebarMinimized, setStartSidebarMinimized] =
    React.useState(false);

  const [mousePosition, setMousePosition] = React.useState({
    mouseX: 0,
    mouseY: 0,
  });
  const [oldTreeWidth, setOldTreeWidth] = React.useState(0);
  const [isHistogramVisible, setIsHistogramVisible] = React.useState(false);

  const [showAdditionalName, setShowAdditionalName] = React.useState(
    (localStorage.getItem("hsaFileViewerShowFileName") ?? "true") === "true",
  );
  const [showComments, setShowComments] = React.useState(
    (localStorage.getItem("hsaFileViewerShowComments") ?? "true") === "true",
  );
  const [showLabel, setShowLabel] = React.useState(
    (localStorage.getItem("hsaFileViewerShowLabel") ?? "true") === "true",
  );
  const [
    useAdditionalNameInsteadOfFilename,
    setUseAdditionalNameInsteadOfFilename,
  ] = React.useState(
    (localStorage.getItem("hsaFileViewerUseDisplayName") ?? "false") === "true",
  );

  // foldertree stuff
  const [topLevelEntities, setTopLevelEntities] = React.useState([]);
  const [expanded, setExpanded] = React.useState([]);
  const [openFolder, setOpenFolder] = React.useState([]);

  // sorting stuff
  const MenuType = Object.freeze({
    Preview: "Preview",
    Files: "Files",
    Cases: "Cases",
    Folder: "Folder",
  });

  const [folderSortBy, setFolderSortBy] = React.useState("name");
  const [folderSortReverse, setFolderSortReverse] = React.useState(false);

  const [filePreviewSortBy, setFilePreviewSortBy] = React.useState(() => {
    try {
      const v = localStorage.getItem("hsaFilePreviewSortBy");
      const allowed = new Set([
        SortByType.Name,
        SortByType.AdditionalName,
        SortByType.Date,
        SortByType.ChangeDate,
        SortByType.Type,
      ]);
      return allowed.has(v) ? v : SortByType.Name;
    } catch {
      return SortByType.Name;
    }
  });
  const [filePreviewSortReverse, setFilePreviewSortReverse] = React.useState(
    () => {
      try {
        return localStorage.getItem("hsaFilePreviewSortReverse") === "true";
      } catch {
        return false;
      }
    },
  );

  const [caseSortBy, setCaseSortBy] = React.useState("name");
  const [caseSortReverse, setCaseSortReverse] = React.useState(false);

  // Persist this popup window's current geometry (position/size) to localStorage with diagnostics
  React.useEffect(() => {
    let expandedTemp = [];
    try {
      const raw = JSON.parse(localStorage.getItem("hsaExpanded")) || [];
      // Normalize slashes to forward for consistent comparisons across OS
      expandedTemp = Array.isArray(raw)
        ? raw.map((p) => (typeof p === "string" ? p.replace(/\\/g, "/") : p))
        : [];
    } catch {
      expandedTemp = [];
    }
    setExpanded(expandedTemp);
    const saveGeometry = () => {
      try {
        const left = Number(window.screenX || window.screenLeft || 0);
        const top = Number(window.screenY || window.screenTop || 0);
        const width = Number(window.outerWidth || 0);
        const height = Number(window.outerHeight || 0);
        // saving popup geometry
        localStorage.setItem("hsaOtherScreenLeft", String(left));
        localStorage.setItem("hsaOtherScreenTop", String(top));
        localStorage.setItem("hsaOtherScreenWidth", String(width));
        localStorage.setItem("hsaOtherScreenHeight", String(height));
      } catch (e) {
        // failed saving popup geometry
      }
    };

    // Save quickly on mount and a few retries in case browser adjusts after open
    const t1 = setTimeout(() => saveGeometry("t+100ms"), 100);
    const t2 = setTimeout(() => saveGeometry("t+300ms"), 300);
    const t3 = setTimeout(() => saveGeometry("t+600ms"), 600);
    // Immediate save, then on resize and before unload
    saveGeometry("immediate");
    const onResize = () => saveGeometry("resize");
    const onBeforeUnload = () => saveGeometry("beforeunload");
    window.addEventListener("resize", onResize);
    window.addEventListener("beforeunload", onBeforeUnload);

    // Poll for window moves (there is no standard 'move' event). Save when position or size changes.
    let lastLeft = Number(window.screenX || window.screenLeft || 0);
    let lastTop = Number(window.screenY || window.screenTop || 0);
    let lastW = Number(window.outerWidth || 0);
    let lastH = Number(window.outerHeight || 0);
    const poll = setInterval(() => {
      try {
        const curLeft = Number(window.screenX || window.screenLeft || 0);
        const curTop = Number(window.screenY || window.screenTop || 0);
        const curW = Number(window.outerWidth || 0);
        const curH = Number(window.outerHeight || 0);
        if (
          curLeft !== lastLeft ||
          curTop !== lastTop ||
          curW !== lastW ||
          curH !== lastH
        ) {
          saveGeometry("poll");
          lastLeft = curLeft;
          lastTop = curTop;
          lastW = curW;
          lastH = curH;
        }
      } catch (e) {
        // ignore polling errors
      }
    }, 400);

    return () => {
      try {
        window.removeEventListener("resize", onResize);
        window.removeEventListener("beforeunload", onBeforeUnload);
        clearTimeout(t1);
        clearTimeout(t2);
        clearTimeout(t3);
        clearInterval(poll);
      } catch (e) {
        /* ignore */
      }
    };
  }, []);

  // #region useEffect
  // Persist preview sort settings centrally so consumers stay in sync
  React.useEffect(() => {
    try {
      localStorage.setItem("hsaFilePreviewSortBy", filePreviewSortBy);
      localStorage.setItem(
        "hsaFilePreviewSortReverse",
        String(filePreviewSortReverse),
      );
    } catch {
      // ignore persistence errors
    }
  }, [filePreviewSortBy, filePreviewSortReverse]);
  React.useEffect(() => {
    let isMounted = true;
    Backend.getCurrentUser((user) => {
      if (!isMounted) return;
      setUserId(user.id);
    });

    FileFormatUtils.refreshSupportedImageFormats();
    setIsHistogramVisible(
      localStorage.getItem("hsaIsHistogramVisible") === "true",
    );
    // Initialize from URL first: honor fileId/caseId in the address bar
    let queryFileId = null;
    try {
      const params = new URLSearchParams(window.location.search || "");
      queryFileId = params.get("fileId");
      if (queryFileId) {
        setActiveFileId(queryFileId);
      }
    } catch (e) {
      // ignore URL parse errors
    }
    // Do not restore activeFileId from localStorage; URL is the source of truth
    Backend.listCases().then((data) => {
      if (!isMounted) return;
      let currentCase = data.cases.find((obj) => obj.data.Id === caseUrlId);
      // Prefer query parameter ?caseId=... if present
      try {
        const params = new URLSearchParams(window.location.search || "");
        const qCaseId = params.get("caseId");
        if (qCaseId) {
          setCaseId(qCaseId);
        } else if (currentCase !== undefined) {
          setCaseId(currentCase?.data.Id);
        } else {
          // Stay on /file_view with no case; don't force navigate here
        }
      } catch (e) {
        // ignore URL errors
      }
      if (data.cases.length === 0) return;
      setCaseList(data);
      setPending(false);
    });
    // init states
    // Check URL flags for popup/minimal UI requests
    try {
      const params = new URLSearchParams(window.location.search || "");
      const sidebarParam = (params.get("sidebar") || "").toLowerCase();
      const noSidebarParam = (params.get("noSidebar") || "").toLowerCase();
      const shouldHide =
        sidebarParam === "hidden" ||
        sidebarParam === "0" ||
        sidebarParam === "false" ||
        noSidebarParam === "1" ||
        noSidebarParam === "true";
      if (shouldHide) {
        // Simulate initial ToggleButtonFV click: store current widths and collapse both preview and sidebar
        setOldPreviewWidth((prev) => (prev > 0 ? prev : previewWidth));
        setOldTreeWidth((prev) => (prev > 0 ? prev : treeWidth));
        setPreviewWidth(0);
        setTreeWidth(0);
        // Keep minimized state so subsequent auto-expands (e.g., on case load) are suppressed
        setStartSidebarMinimized(true);
      }
    } catch (e) {
      // ignore URL parse errors
    }
    // Do not restore activeFileId from localStorage; URL is the source of truth
    const initActiveFilePath = localStorage.getItem(
      "hsaFileViewerActiveFilePath",
    );
    const initSelectedFolder = JSON.parse(
      localStorage.getItem("hsaSelectedFolder"),
    );

    // check if path has case id after "file_view/"
    const path = (window.location.pathname || "").split("/");
    const endOfPath = path[path.length - 1];

    if (endOfPath === "file_view" || endOfPath === "") {
      // If URL specifies a case or file, do not restore stale local selections
      let hasUrlCase = false;
      let hasUrlFile = false;
      try {
        const params = new URLSearchParams(window.location.search || "");
        hasUrlCase = params.has("caseId") || !!caseUrlId;
        hasUrlFile = params.has("fileId");
      } catch (e) {
        // ignore URL parse errors
      }

      if (!hasUrlCase && !hasUrlFile) {
        if (initActiveFilePath) {
          setActiveFilePath(initActiveFilePath);
        }
        if (initSelectedFolder) {
          setSelectedFolder(initSelectedFolder);
        }
      }
    }

    return () => {
      isMounted = false;
    };
  }, []);

  // Manual history syncing removed: rely on path-based navigation only

  // Keep local state/localStorage in sync when activeFileId changes (no URL writes)
  React.useEffect(() => {
    // When activeFileId is cleared (e.g., empty dir selection), remove it from URL and localStorage
    if (activeFileId === null) {
      // Proactively clear any renderer state so nothing remains visible
      try {
        setActiveFileName(null);
        setActiveFilePath(null);
        setSplitscreenFileIds([]);
        setRendererDict({});
        setZoomObjectDict({});
        setCommentLayer({});
      } catch (e) {
        // ignore state/localStorage errors
      }
      try {
        localStorage.removeItem("hsaActiveFileId");
      } catch (e) {
        // ignore persistence errors
      }
      return;
    }
    // Persist active file id to localStorage so other views or later reloads can reference it if needed
    try {
      localStorage.setItem("hsaActiveFileId", String(activeFileId));
    } catch (e) {
      // ignore persistence errors
    }
  }, [activeFileId]);

  React.useEffect(() => {
    rendererDictRef.current = rendererDict;
  }, [rendererDict]);

  React.useEffect(() => {
    if (!activeFileId || zoomObjectDict === null) return;
    // setContainerKey(new Date().getTime());
  }, [zoomObjectDict]);

  React.useEffect(() => {
    if (!omeDict || !activeFileId) return;
    const newTool = tools[activeTool];
    if (!newTool) {
      if (tool) {
        tool.exit();
      }
      setTool(null);
      return;
    }
    // Create a fresh comment layer object if it doesn't exist
    const currentCommentLayer = commentLayer[activeFileId] || {
      commentRois: [],
    };

    const toolObject = {
      ome: omeDict[activeFileId],
      commentLayer: currentCommentLayer,
      heatMapOpacity,
      setHeatMapOpacity,
    };
    newTool.setLayer(toolObject);
    setTool(newTool);
  }, [omeDict, activeTool]);

  React.useEffect(() => {
    if (activeFileId === null) return;
    setSceneDataList((prevList) =>
      prevList.map((scene) =>
        scene.id === activeFileId ? { ...scene, hasSeen: true } : scene,
      ),
    );
  }, [activeFileId]);

  React.useEffect(() => {
    if (selectedFolder === null) return;
    localStorage.setItem("hsaSelectedFolder", JSON.stringify(selectedFolder));
    // New behavior: do not auto-populate preview when a folder is selected.
    // The preview (pathList) will only be set when a specific file is clicked.
  }, [selectedFolder]);

  React.useEffect(() => {
    function handleMouseMove(e) {
      setMousePosition({ mouseX: e.pageX, mouseY: e.pageY });
    }

    document.addEventListener("mousemove", handleMouseMove);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
    };
  });

  React.useEffect(() => {
    if (caseId === null || caseId === undefined) return;
    if (!caseList || !caseList.cases) return;
    getFilePathsFromCase(caseId);
  }, [caseId, caseList]);

  React.useEffect(() => {
    // On initial mount, caseId may be undefined until URL/listCases hydrate it.
    // Do not force-clear it to null to avoid dropping a caseId present in the URL.
    if (caseId === undefined) return;
    // Also normalize accidental literal 'undefined' string
    if (caseId === "undefined") {
      setCaseId(null);
      return;
    }
    if (caseId === null) {
      setPathList([]);
      return;
    }
    // No history/URL updates here — rely on router path params and state
  }, [caseId]);

  // Read caseId and fileId from URL on navigation (back/forward) — watch both pathname and search
  React.useEffect(() => {
    const norm = (s) => {
      if (!s || s === "undefined" || s === "null") return null;
      return s;
    };

    let newCaseId = null;
    let newFileId = null;

    // Prefer explicit query parameters if present
    try {
      const search = location?.search || window.location.search || "";
      const params = new URLSearchParams(search);
      const qCaseId = params.get("caseId");
      const qFileId = params.get("fileId");
      if (qCaseId) newCaseId = norm(qCaseId);
      if (qFileId) newFileId = norm(qFileId);
    } catch (e) {
      // ignore URL parse errors
    }

    // If no query params were provided, fall back to path segments
    if (!newCaseId && !newFileId) {
      const pathname = location?.pathname || window.location.pathname || "";
      const parts = pathname.split("/").filter(Boolean);

      const idxFileView = parts.indexOf("file_view");
      const idxCases = parts.indexOf("cases");

      if (idxFileView >= 0) {
        const rest = parts.slice(idxFileView + 1);
        let p = 0;
        // case segment may be labeled or implicit
        if (rest[p] === "case") p++;
        newCaseId = norm(rest[p]);
        p++;
        // file segment may be labeled or implicit
        if (rest[p] === "file") p++;
        newFileId = norm(rest[p]);
      } else if (idxCases >= 0) {
        const rest = parts.slice(idxCases + 1);
        newCaseId = norm(rest[0]);
        newFileId = null;
      }
    }

    // Apply updates only when changed
    if (newCaseId !== caseId) setCaseId(newCaseId);
    if (newFileId !== activeFileId) setActiveFileId(newFileId);

    // If fileId is absent in the URL, proactively clear any lingering per-file state
    // regardless of whether a caseId is present. This prevents auto-reselection
    // via effects that might derive from an existing activeFilePath.
    if (!newFileId) {
      if (activeFilePath) setActiveFilePath(null);
      if (activeFileName) setActiveFileName(null);
    }

    // If we navigated to /file_view without any query (?caseId / ?fileId),
    // clear any residual folder/preview state so the list is empty.
    try {
      const pathname = location?.pathname || window.location.pathname || "";
      const isFileView = /(^|\/)file_view(\/|$)/i.test(pathname);
      const noQueryIds = !newCaseId && !newFileId;
      if (isFileView && noQueryIds) {
        // Ensure active selection is cleared when fileId is removed from URL
        if (activeFileId !== null) setActiveFileId(null);
        if (activeFilePath) setActiveFilePath(null);
        if (activeFileName) setActiveFileName(null);
        if (selectedFolder !== null) setSelectedFolder(null);
        if (Array.isArray(pathList) && pathList.length > 0) setPathList([]);
        if (Array.isArray(sceneDataList) && sceneDataList.length > 0)
          setSceneDataList([]);
      }
    } catch (e) {
      // ignore path clearing errors
    }
  }, [location?.pathname, location?.search]);

  React.useEffect(() => {
    if (activeFileId === null) return;
    // Resolve path/name from the backend when missing.
    // This must not be gated by case selection because the URL can set a fileId
    // while other state (caseId/path) is still hydrating.
    let cancelled = false;
    const requestedFileId = activeFileId;
    if (!activeFilePath || !activeFileName) {
      Backend.getFileInfo(requestedFileId)
        .then((fileInfo) => {
          if (cancelled) return;
          if (requestedFileId !== activeFileId) return;
          if (!fileInfo) return;
          if (
            fileInfo.relativePath &&
            fileInfo.relativePath !== activeFilePath
          ) {
            setActiveFilePath(fileInfo.relativePath);
          }
          // Always refresh the name on fileId changes; otherwise it can remain stale
          // (e.g., when switching files quickly while a previous name is still set).
          if (fileInfo.fileName && fileInfo.fileName !== activeFileName) {
            setActiveFileName(fileInfo.fileName);
          }
        })
        .catch((e) => {
          // Non-fatal: file can still be rendered via other state paths
          console.warn("Failed to resolve file info:", e?.message || e);
        });
    }
    // Do not persist activeFileId to localStorage; URL is the source of truth
    // Remember which tool was active before switching files so we can restore it
    const prevActiveTool = activeTool;
    const shouldRestoreTool =
      prevActiveTool === FileViewerTools.Comment ||
      prevActiveTool === FileViewerTools.ViewHeatMap ||
      prevActiveTool === FileViewerTools.Stream;
    // Temporarily clear the active tool while we load per-file data like the comment layer
    setActiveTool(FileViewerTools.None);
    Backend.getCommentLayer(activeFileId)
      .then((commentLayer) => {
        if (!commentLayer) return;
        if (!commentLayer.commentRois) {
          commentLayer.commentRois = [];
        }
        setCommentLayer((prevState) => ({
          ...prevState,
          [activeFileId]: commentLayer,
        }));
        // Restore previously active tool (Comment, Heatmap, or Stream) after loading
        if (shouldRestoreTool) setActiveTool(prevActiveTool);
      })
      .catch((error) => {
        console.error("Error fetching comment layer:", error);
        // Even if comment layer fails to load, restore the previously active tool state
        if (shouldRestoreTool) setActiveTool(prevActiveTool);
      });

    return () => {
      cancelled = true;
    };
  }, [activeFileId, caseId, activeFilePath]);

  // Keep activeFileName in sync with the resolved path.
  // Several flows set activeFilePath (URL restore, folder selection, etc.) without
  // explicitly updating activeFileName.
  React.useEffect(() => {
    if (!activeFilePath) return;
    const derivedName = String(activeFilePath).split(/[/\\]/).pop();
    if (derivedName && derivedName !== activeFileName) {
      setActiveFileName(derivedName);
    }
  }, [activeFilePath, activeFileName]);

  React.useEffect(() => {
    window.zoomToRect = (rect) => {
      let width = rect.right - rect.left;
      let height = rect.bottom - rect.top;
      if (rendererDict[activeFileId]) {
        rendererDict[activeFileId].zoomToRect(
          rect.top - height,
          rect.left - width,
          rect.right + width,
          rect.bottom + height,
        );
      }
    };

    return () => {
      window.zoomToRect = undefined; // Cleanup
    };
  }, [activeFileId, rendererDict]); // Add dependencies here

  // load possibly saved histogram config from the backend
  React.useEffect(() => {
    // Guard against race conditions where the file path isn't set yet
    if (!activeFileName || !activeFilePath || !omeDict[activeFileId]) return;
    const channels = omeDict[activeFileId].channels;

    const histogramConfig = {
      channels: channels.map((c) => ({
        color: c.color || "#ffffff",
        enabled: true,
        gamme: 1,
        max: parseInt(c.high * 255, 10) || 255,
        min: parseInt(c.low * 255, 10) || 0,
        name: c.name || "Channel " + c.id,
        type: c.type || "brightfield",
      })),
      nChannels: channels.length,
    };

    Backend.getHistogramConfig(activeFilePath, histogramConfig)
      .then((response) => {
        if (response?.histogramConfigString) {
          const fileHistogramConfig = JSON.parse(
            response.histogramConfigString,
          );
          const newHistogramConfig = {
            ...histogramConfig,
            channels: fileHistogramConfig.channels,
          };
          newHistogramConfig.channels.forEach((channel) => {
            channel.type = channel.type
              ? channel.type
              : newHistogramConfig.channels.length > 1
                ? "fluorescence"
                : "brightfield";
          });

          onChangeChannels(newHistogramConfig);
        }
      })
      .catch((err) => {
        // This is non-fatal and can occasionally happen during quick file switches
        console.warn(
          "Skipping histogram config load (likely transient):",
          err?.message || err,
        );
      });
  }, [omeDict, activeFileName, activeFileId]);

  React.useEffect(() => {
    updateDimensions();
  }, [splitscreenFileIds]);

  // #endregion useEffect

  const onSaveScreenshot = () => {
    let img = rendererDictRef.current[activeFileId].canvas;

    img = img.toDataURL("image/jpeg", 0.9);
    let link = document.createElement("a");
    const fileNameNoExt = (activeFilePath || activeFileName || "screenshot")
      .split(/[/\\]/)
      .pop()
      .replace(/\.[^/.]+$/, "");

    let screenShotName = `${fileNameNoExt}.jpg`;

    link.download = screenShotName;
    link.href = img.replace("image/jpeg", "image/octet-stream");
    link.click();
  };

  const updatePreviewWidth = (newWidth) => {
    setTreeWidth(newWidth);
    localStorage.setItem("fileViewerSidebarWidth", newWidth);
  };

  const getMousePosition = () => {
    return [mousePosition.mouseX, mousePosition.mouseY];
  };

  const toggleHistogram = () => {
    localStorage.setItem("hsaIsHistogramVisible", !isHistogramVisible);
    setIsHistogramVisible(!isHistogramVisible);
  };

  const updateExpanded = (newExpanded) => {
    // Normalize before persisting
    const norm = Array.isArray(newExpanded)
      ? newExpanded.map((p) =>
          typeof p === "string" ? p.replace(/\\/g, "/") : p,
        )
      : [];
    setExpanded(norm);
    localStorage.setItem("hsaExpanded", JSON.stringify(norm));
  };

  /**
   * Rotates the currently active renderer by the specified degree.
   *
   * The rotation state (`rotState`) is constrained to one of the allowed values: 0, 90, 180, or 270 degrees.
   * If the resulting rotation is not one of these, it is clamped to the nearest allowed value.
   * After updating the rotation state, the renderer is reset.
   *
   * @param {number} deg - The degree to rotate by. Should be either 90 or -90.
   */
  const onRotate = (deg) => {
    // rotState can only be 0, 90, 180, 270; deg can only be 90 or -90
    let r = rendererDictRef.current[activeFileId];
    if (!r) return;
    // Normalize input to a number and clamp to multiples of 90
    const step = Number(deg);
    let currentRot = r.state.rotState || 0;
    let newRot = (currentRot + (Number.isFinite(step) ? step : 90)) % 360;
    if (newRot < 0) newRot += 360;
    // Clamp to allowed values
    if (![0, 90, 180, 270].includes(newRot)) {
      // Find the closest allowed value
      const allowed = [0, 90, 180, 270];
      newRot = allowed.reduce((prev, curr) =>
        Math.abs(curr - newRot) < Math.abs(prev - newRot) ? curr : prev,
      );
    }
    // remember the rotation state in localStorage
    localStorage.setItem("hsaFileViewerRotState" + activeFileId, newRot);
    r.setMountedState({ rotState: newRot }, () => {
      r.loadAllTiles();
    });
    // Rotate the saved zoom ROI to keep the same scene content in view after rotation
    setZoomObjectDict((prevState) => {
      if (!prevState || !prevState[activeFileId]) return prevState;
      const rNow = rendererDictRef.current[activeFileId];
      if (!rNow) return prevState;
      const oldZoom = rNow.props.persistentStorage.load(
        "zoomObjectFV" + rNow.props.fileId,
      );
      // Guard against missing/invalid zoom object
      if (
        !oldZoom ||
        typeof oldZoom.zoomBottom !== "number" ||
        typeof oldZoom.zoomTop !== "number" ||
        typeof oldZoom.zoomLeft !== "number" ||
        typeof oldZoom.zoomRight !== "number"
      ) {
        return prevState;
      }

      // Determine rotation delta in [0,90,180,270]
      const oldRot = currentRot % 360;
      const delta = (newRot - oldRot + 360) % 360;

      // Use the original image dimensions from OME (pre-rotation)
      // Don't use rNow.vw/vh here because they swap when rotState is 90/270
      const vw = rNow.props.ome.sizeX; // width in image space
      const vh = rNow.props.ome.sizeY; // height in image space

      // Compute the rotated rectangle depending on delta
      let newObject = { ...oldZoom };
      if (delta === 90) {
        // 90° CW: (x,y) -> (vh - y, x)
        newObject = {
          zoomRoi: true,
          zoomLeft: vh - oldZoom.zoomBottom,
          zoomRight: vh - oldZoom.zoomTop,
          zoomTop: oldZoom.zoomLeft,
          zoomBottom: oldZoom.zoomRight,
        };
      } else if (delta === 180) {
        // 180°: (x,y) -> (vw - x, vh - y)
        newObject = {
          zoomRoi: true,
          zoomLeft: vw - oldZoom.zoomRight,
          zoomRight: vw - oldZoom.zoomLeft,
          zoomTop: vh - oldZoom.zoomBottom,
          zoomBottom: vh - oldZoom.zoomTop,
        };
      } else if (delta === 270) {
        // 270° CW (== -90): (x,y) -> (y, vw - x)
        newObject = {
          zoomRoi: true,
          zoomLeft: oldZoom.zoomTop,
          zoomRight: oldZoom.zoomBottom,
          zoomTop: vw - oldZoom.zoomRight,
          zoomBottom: vw - oldZoom.zoomLeft,
        };
      } else {
        // 0° delta: keep as is
        newObject = { ...oldZoom };
      }

      // Persist and reflect in state
      rNow.props.persistentStorage.save(
        "zoomObjectFV" + rNow.props.fileId,
        newObject,
      );
      return {
        ...prevState,
        [activeFileId]: newObject,
      };
    });

    setContainerKey(new Date().getTime());
  };

  /**
   * Saves the current histogram configuration for the active file type and user.
   */
  const onSave = () => {
    // go through histogramConfigs[activeFileId].channels and add each name to channelNames
    if (histogramConfigs[activeFileId]) {
      const histogramConfig = histogramConfigs[activeFileId];

      // Prefer path (stable + includes extension); fall back to name for backward compatibility.
      const histogramFileKey = activeFilePath || activeFileName;
      if (!histogramFileKey) return;

      Backend.saveHistogramConfig(histogramFileKey, histogramConfig)
        .then((response) => {
          if (response) {
            window.showSuccessSnackbar(
              "Histogram configuration saved successfully",
            );
          }
        })
        .catch((error) => {
          console.log(
            "Error saving histogram configuration:",
            histogramFileKey,
            histogramConfig,
            error,
          );
          window.openErrorDialog(
            "Error saving histogram configuration:",
            error.message,
          );
        });
    }
    // log commentRois for the activeFileId
    if (commentLayer[activeFileId]) {
      Backend.saveCommentLayer(activeFileId, commentLayer[activeFileId])
        .then((response) => {
          if (response) {
            window.showSuccessSnackbar("Comment layer saved successfully");
          }
        })
        .catch((error) => {
          window.openErrorDialog("Error saving comment layer:", error.message);
        });
    }
  };

  const getFilePathsFromCase = (caseId) => {
    if (!caseId) return;
    if (!caseList || !caseList.cases || !Array.isArray(caseList.cases)) return;
    const foundCase = caseList.cases.find((obj) => obj?.data?.Id === caseId);
    if (!foundCase) return;
    try {
      const newPathList = (foundCase.filePaths || []).map((path) => ({
        path,
        type: "file",
      }));
      setPathList(newPathList);
      if (foundCase.data) {
        foundCase.data.Files = foundCase.filePaths;
        setSelCase(foundCase.data);
      }
      // Only auto-expand the dock if we didn't start in minimized mode
      if (!startSidebarMinimized) {
        setTreeWidth(870);
      }
    } catch (e) {
      // Fail silently; better to keep UI responsive
      // Optionally could log e
    }
  };

  React.useEffect(() => {
    if (!selCase) return;
    if (!selCase.Files || selCase.Files.length === 0) return;
  }, [selCase]);

  // If no case is active AND no path/folder is selected, ensure scene list is empty
  React.useEffect(() => {
    const noCaseActive = caseId === null || caseId === undefined;
    const noPathSelected =
      !activeFilePath &&
      (!selectedFolder || !selectedFolder.path) &&
      (!Array.isArray(pathList) || pathList.length === 0);
    if (noCaseActive && noPathSelected) {
      if (Array.isArray(sceneDataList) && sceneDataList.length > 0) {
        setSceneDataList([]);
      }
    }
  }, [caseId, activeFilePath, selectedFolder, pathList, sceneDataList]);

  const getScenesForFile = async (isMounted) => {
    if (isMounted) setIsScenesLoading(true);
    try {
      // Use batch endpoint to reduce round-trips
      const results = await Backend.getFileIdData(pathList.map((p) => p.path));
      const tempSceneObj = {};
      results.forEach((sceneData, index) => {
        const path = pathList[index].path;
        if (sceneData && sceneData.sceneIds && sceneData.sceneIds.length > 0) {
          tempSceneObj[path] = sceneData.sceneIds;
        } else {
          tempSceneObj[path] = [];
        }
      });
      if (isMounted) {
        // setSceneList((prevSceneList) => ({ ...prevSceneList, ...tempSceneObj }));
        setSceneList({ ...tempSceneObj });
      }
      return isMounted;
    } catch (err) {
      console.error("Failed to fetch scenes:", err);
      if (isMounted) {
        window.showErrorSnackbar?.("Failed to load file previews");
      }
      return isMounted;
    } finally {
      if (isMounted) setIsScenesLoading(false);
    }
  };

  const updateDimensions = () => {
    let savedT = 0;

    // Persist the current view (scale + center) before forcing a remount.
    // Otherwise we can reload a stale zoomObject and appear to "zoom out".
    try {
      Object.values(rendererDict).forEach((r) => {
        if (r && typeof r.zoomMoveAction === "function") {
          r.zoomMoveAction();
        }
      });
    } catch (e) {
      // ignore
    }

    setContainerKey(new Date().getTime());
    if (rendererDict[activeFileId]?.state) {
      savedT = rendererDict[activeFileId].state.t;
    }
    if (savedT > 0 && rendererDict[activeFileId])
      rendererDict[activeFileId].updateT(savedT);
    handleSavedZoomObjects();
  };

  const onChainSplitscreen = (fileId, e) => {
    if (!chainListFileIds.includes(fileId)) {
      setChainListFileIds([...chainListFileIds, fileId]);
    } else {
      // Remove fileId if present
      setChainListFileIds(chainListFileIds.filter((id) => id !== fileId));
    }

    if (e) {
      e.stopPropagation();
    }
  };

  const handleSavedZoomObjects = () => {
    if (splitscreenFileIds.length === 0) return;
    let newZoomObjectDict = {};
    for (const fileId of splitscreenFileIds) {
      let zoomData = props.persistentStorage.load("zoomObjectFV" + fileId);
      if (zoomData) {
        newZoomObjectDict[fileId] = structuredClone(zoomData);
      } else {
        newZoomObjectDict[fileId] = {
          zoomRoi: false,
          zoomLeft: 0,
          zoomRight: 0,
          zoomTop: 0,
          zoomBottom: 0,
          zoomScale: undefined,
          zoomCenterX: undefined,
          zoomCenterY: undefined,
        };
      }
    }
    setZoomObjectDict((prevState) => ({ ...prevState, ...newZoomObjectDict }));
  };

  const emitRendererEvent = (data) => {
    EventBus.emit("chainEvent", data);
  };

  /**
   * Updates the histogram configuration for the active file and updates the renderer if it exists.
   *
   * @param {Object} newHistogramConfig - The new histogram configuration to be applied.
   */
  const onChangeChannels = (newHistogramConfig) => {
    setHistogramConfigs((prevState) => ({
      ...prevState,
      [activeFileId]: newHistogramConfig,
    }));
  };

  React.useEffect(() => {
    if (!activeFileId || !rendererDict[activeFileId]) return;
    const newHistogramConfig = histogramConfigs[activeFileId];
    rendererDict[activeFileId].updateFLChannels(newHistogramConfig);
  }, [histogramConfigs]);

  React.useEffect(() => {
    const newfileIds = [];
    for (const fileId of Object.keys(rendererDict)) {
      // check if the key exists in commentLayer
      if (rendererDict[fileId] && !commentLayer[fileId]) {
        newfileIds.push(fileId);
      }
    }
    if (newfileIds.length === 0) return;
    // for each new fileId, create a new commentLayer with { commentRois: [] } as content
    const newCommentLayer = {};
    newfileIds.forEach((fileId) => {
      newCommentLayer[fileId] = { commentRois: [] };
    });
    setCommentLayer((prevState) => ({
      ...prevState,
      ...newCommentLayer,
    }));
  }, [rendererDict]);

  const resetRenderer = () => {
    setActiveFileId(null);
    setActiveFileName(null);
    setActiveFilePath(null);
    setContainerKey(new Date().getTime());
    setRendererDict({});
    setCommentLayer({});
    setSceneList({});
    setSelectedFolder(null);
    setSplitscreenFileIds([]);
    setZoomObjectDict({});
  };

  const toggleLayoutElement = (elementKey) => {
    setLayoutStateObject((prevState) => {
      const newLayoutStateObject = {
        ...prevState,
        [elementKey]: !prevState[elementKey],
      };
      localStorage.setItem(
        "hsaFileViewerLayoutState",
        JSON.stringify(newLayoutStateObject),
      );
      return newLayoutStateObject;
    });
  };

  /**
   * Toggles the currently active tool in the file viewer sidebar.
   * If the specified tool is already active, it deactivates it by setting to `FileViewerTools.None`.
   * Otherwise, it activates the specified tool.
   *
   * @param {string} toolName - The name of the tool to toggle as active.
   */
  const toggleActiveTool = (toolName) => {
    setActiveTool((prevActiveTool) => {
      if (prevActiveTool === toolName) {
        return FileViewerTools.None;
      } else {
        return toolName;
      }
    });
  };

  /**
   * Handles the import of a rotation object for the currently active file.
   *
   * Initiates the import process, sets the initial import progress, and starts polling
   * the backend for progress updates. When the import reaches 100%, the page reloads.
   * Handles errors by displaying an error dialog and resetting the progress.
   *
   * @param {Object} rotationObject - The rotation data to be imported.
   */
  const onImportRotation = (rotationObject) => {
    setProcessingRotation(true);
    setImportProgress(0);
    Backend.importRotation(activeFileId, rotationObject).then((response) => {
      if (response) {
        Backend.getFileInfo(activeFileId).then((fileInfo) => {
          if (!fileInfo) return;
        });

        const pollInterval = setInterval(() => {
          Backend.getFileInfo(activeFileId)
            .then((fileInfo) => {
              if (fileInfo.progress >= 0) {
                setImportProgress(fileInfo.progress);
              } else {
                setImportProgress(0);
              }
              if (fileInfo && fileInfo.progress === 100) {
                clearInterval(pollInterval);

                window.location.reload(); // Reload page when progress is 100%
                return;
              }
            })
            .catch((error) => {
              console.error("Error fetching file info during import:", error);
              clearInterval(pollInterval);
              window.openErrorDialog("Error fetching file info during import");
              setImportProgress(-1); // Reset progress on error
              setProcessingRotation(false);
            });
        }, 1000);

        // Cleanup interval on component unmount or when activeFileId changes
        return () => clearInterval(pollInterval);
      } else {
        window.openErrorDialog("Error importing rotation");
      }
    });
  };

  /**
   * Handles stopping the rotation import process.
   *
   * - Sets the processing state to false.
   * - Resets the import progress indicator.
   * - Calls the backend to stop the rotation import for the active file.
   * - If the backend call fails, displays an error dialog to the user.
   */
  const onStopRotationImport = (fileId) => {
    setProcessingRotation(false);
    setImportProgress(-1);
    Backend.stopRotationImport(fileId).then((response) => {
      if (!response) {
        window.openErrorDialog("Error stopping rotation import");
      }
    });
  };

  // Define all shortcuts and their handlers in one array so we can easily manage them.
  // Each shortcut has a test function to check if the key combination matches
  // and a handler function to execute the corresponding action.
  const shortcutMap = [
    {
      test: (e) => e.shiftKey && e.code === "KeyC",
      handler: () => toggleActiveTool(FileViewerTools.Comment),
    },
    {
      test: (e) => e.shiftKey && e.code === "KeyH",
      handler: () => toggleActiveTool(FileViewerTools.ViewHeatMap),
    },
    {
      test: (e) => e.shiftKey && e.code === "KeyP",
      handler: () => onSaveScreenshot(),
    },
    {
      test: (e) => e.shiftKey && (e.key === "*" || e.key === "+"),
      handler: (fileIds) =>
        emitRendererEvent({
          type: "zoomOneToN",
          value: 1,
          fileIds: fileIds,
        }),
    },
    {
      test: (e) =>
        e.shiftKey && (e.key === "0" || e.key === "=" || e.code === "Numpad0"),
      handler: (fileIds) =>
        emitRendererEvent({ type: "zoomFit", fileIds: fileIds }),
    },
    {
      test: (e) => e.key === "+" && !e.shiftKey,
      handler: (fileIds) =>
        emitRendererEvent({
          type: "zoomDelta",
          value: -1,
          fileIds: fileIds,
        }),
    },
    {
      test: (e) => e.key === "-" && !e.shiftKey,
      handler: (fileIds) =>
        emitRendererEvent({
          type: "zoomDelta",
          value: 1,
          fileIds: fileIds,
        }),
    },
    {
      test: (e) => e.ctrlKey && e.key === "S",
      handler: () => onSave(),
    },
  ];

  // Register the keydown event listener for shortcuts
  // This effect will run once when the component mounts
  // and will clean up the event listener when the component unmounts or dependencies change.
  // useEffect is used, so the handler is using the latest state and props.
  React.useEffect(() => {
    const isTypingContext = (el) => {
      if (!el) return false;
      // Walk up in case of nested elements inside a contenteditable / input wrapper
      const editable = el.closest('textarea, input, [contenteditable="true"]');
      if (!editable) return false;
      // Allow non-textual inputs to still use shortcuts
      if (editable.tagName === "INPUT") {
        const skipTypes = [
          "checkbox",
          "radio",
          "button",
          "submit",
          "range",
          "color",
          "file",
        ];
        if (skipTypes.includes(editable.type)) return false;
      }
      if (editable.readOnly || editable.disabled) return false;
      return true;
    };

    const handleKeyDown = (e) => {
      if (isTypingContext(e.target)) return; // Do not trigger shortcuts while typing
      let fileIds = chainListFileIds;
      if (activeFileId && !fileIds.includes(activeFileId)) {
        fileIds = [activeFileId];
      }
      for (const shortcut of shortcutMap) {
        if (shortcut.test(e)) {
          e.preventDefault();
          shortcut.handler(fileIds);
          break;
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    activeFileId,
    chainListFileIds,
    toggleActiveTool,
    onSaveScreenshot,
    emitRendererEvent,
    onSave,
    FileViewerTools.Comment,
    FileViewerTools.ViewHeatMap,
  ]);

  const context = {
    activeFileId,
    activeFileName,
    activeFilePath,
    activeTool,
    caseId,
    caseList,
    caseSortBy,
    caseSortReverse,
    chainListFileIds,
    commentLayer,
    commentRois,
    containerKey,
    emitRendererEvent,
    expanded,
    filePreviewSortBy,
    filePreviewSortReverse,
    folderSortBy,
    folderSortReverse,
    getScenesForFile,
    heatMapOpacity,
    histogramConfigs,
    importProgress,
    isHistogramVisible,
    layoutStateObject,
    tempHideScaleBar,
    MenuType,
    onSaveScreenshot,
    oldPreviewWidth,
    oldTreeWidth,
    omeDict,
    onChainSplitscreen,
    onChangeChannels,
    onImportRotation,
    onStopRotationImport,
    onRotate,
    onSave,
    openFolder,
    pathList,
    pending,
    previewWidth,
    processingRotation,
    rendererDict,
    resetRenderer,
    sceneDataList,
    sceneList,
    selCase,
    selectedFolder,
    setActiveFileId,
    setActiveFileName,
    setActiveFilePath,
    setCaseId,
    setCaseList,
    setCaseSortBy,
    setCaseSortReverse,
    setChainListFileIds,
    setCommentRois,
    setContainerKey,
    setFilePreviewSortBy,
    setFilePreviewSortReverse,
    setFolderSortBy,
    setFolderSortReverse,
    setHistogramConfigs,
    setImportProgress,
    setTempHideScaleBar,
    setOldPreviewWidth,
    setOldTreeWidth,
    setOmeDict,
    setOpenFolder,
    setPathList,
    setPreviewWidth,
    setRendererDict,
    setSceneDataList,
    setSceneList,
    setSelCase,
    setSelectedFolder,
    setShowAdditionalName,
    setShowComments,
    setShowLabel,
    setUseAdditionalNameInsteadOfFilename,
    setSplitscreenFileIds,
    setTopLevelEntities,
    setTreeWidth,
    setZoomObjectDict,
    showAdditionalName,
    showComments,
    showLabel,
    useAdditionalNameInsteadOfFilename,
    SortByType,
    splitscreenFileIds,
    toggleActiveTool,
    toggleHistogram,
    toggleLayoutElement,
    tool,
    topLevelEntities,
    treeWidth,
    updateDimensions,
    updateExpanded,
    updatePreviewWidth,
    userId,
    zoomObjectDict,
  };

  return (
    <FileViewerContext.Provider value={context}>
      <Grid container className={classes.root}>
        <Backdrop
          open={isScenesLoading}
          sx={{ zIndex: (theme) => theme.zIndex.drawer + 1 }}
        >
          <CircularProgress />
        </Backdrop>
        <FileViewerSplitscreenManager getMousePosition={getMousePosition} />
        <FileViewerSideBar selCase={selCase} toolbarOnly={toolbarOnlySidebar} />
      </Grid>
    </FileViewerContext.Provider>
  );
}

FileViewer.propTypes = {
  persistentStorage: PropTypes.object,
  tiles: PropTypes.object,
};

export default withPersistentStorage(withTiles(FileViewer));
